import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import type { RootState } from 'redux/reemaStore.util';
import { getRaceEthnicityOptions } from 'redux/slices/metaSlice.slice';
import { getMemberFilters } from 'redux/slices/memberSliceActions.thunk';
import { getTeam } from 'redux/slices/usersSlice.slice';
import { verifyUser } from 'redux/slices/actingUserSlice.slice';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import {
  isReferenceDataFresh,
  memberFiltersCacheKey,
  shouldDispatchVerifyUser,
} from 'redux/utils/referenceDataCache.util';

/** Compatible with carePortalMiddleware's loosely typed dispatch. */
type AppThunk = ThunkAction<void, unknown, unknown, UnknownAction>;

/**
 * Loads race-ethnicity catalog options when missing, loading, or stale.
 * @param staleMs - Optional override for the session stale window.
 * @returns Thunk that may dispatch getRaceEthnicityOptions.
 */
export function ensureRaceEthnicityOptions(staleMs?: number): AppThunk {
  return (dispatch, getState) => {
    const { raceEthnicityOptions } = (getState() as RootState).meta;
    if (
      isReferenceDataFresh(
        raceEthnicityOptions.status,
        raceEthnicityOptions.fetchedAtMs,
        staleMs
      )
    ) {
      return;
    }
    if (raceEthnicityOptions.status === 'loading') {
      return;
    }
    void dispatch(getRaceEthnicityOptions({}));
  };
}

/**
 * Runs verifyUser when the acting user is not yet successfully loaded, or when forced.
 * No-ops while a verify is in flight (even with force) so concurrent callers share one
 * thunk lifecycle and do not each emit `verifyUser.fulfilled` side effects.
 * @param options - Pass force true after IAM/permission changes that require a fresh verify.
 * @returns Thunk that may dispatch verifyUser.
 */
export function ensureVerifyUser(
  options: { force?: boolean } = {}
): AppThunk {
  return (dispatch, getState) => {
    const { force = false } = options;
    const { status, user, verifyInFlight } = (getState() as RootState)
      .actingUser;
    if (
      !shouldDispatchVerifyUser({
        status,
        userId: user?.id,
        force,
        verifyInFlight,
      })
    ) {
      return;
    }
    void dispatch(verifyUser({}));
  };
}

/**
 * Loads member filters for a `named` field list when missing, loading, or stale.
 * @param named - Comma-separated filter keys for GET /users/filters.
 * @param staleMs - Optional override for the session stale window.
 * @returns Thunk that may dispatch getMemberFilters.
 */
export function ensureMemberFilters(
  named: string,
  staleMs?: number
): AppThunk {
  return (dispatch, getState) => {
    const key = memberFiltersCacheKey(named);
    const entry = (getState() as RootState).members.filtersByNamedKey[key];
    if (
      entry &&
      isReferenceDataFresh(entry.status, entry.fetchedAtMs, staleMs)
    ) {
      return;
    }
    if (entry?.status === 'loading') {
      return;
    }
    void dispatch(getMemberFilters({ params: { named } }));
  };
}

/**
 * Loads the all-viewable team list when missing, loading, or stale.
 * Call sites should always dispatch this (do not gate on `viewable === null`);
 * the thunk skips when the all-viewable session stamp is still fresh.
 * Does not cover the allCareStaff call-invite variant.
 * Background refresh keeps `teamLoadingStatus` at success (see getTeam.pending)
 * and uses `teamAllViewableInFlight` so concurrent ensures coalesce without a UI spinner.
 * @param staleMs - Optional override for the session stale window.
 * @returns Thunk that may dispatch getTeam with allViewable true.
 */
export function ensureTeamAllViewable(staleMs?: number): AppThunk {
  return (dispatch, getState) => {
    const {
      teamLoadingStatus,
      teamAllViewableFetchedAtMs,
      teamAllViewableInFlight,
      viewable,
    } = (getState() as RootState).users;
    if (
      viewable !== null &&
      isReferenceDataFresh(
        teamLoadingStatus,
        teamAllViewableFetchedAtMs,
        staleMs
      )
    ) {
      return;
    }
    // `teamAllViewableInFlight` covers background refresh while status stays `success`.
    // Call-invite / bare getTeam must not clear this flag.
    if (teamAllViewableInFlight || teamLoadingStatus === 'loading') {
      return;
    }
    void dispatch(getTeam({ params: { allViewable: true } }));
  };
}

addToEffectsMiddleware(addEffect => {
  addEffect({
    action: verifyUser.fulfilled,
    effect: async ({ dispatch }) => {
      dispatch(ensureRaceEthnicityOptions());
    },
  });
});
